/**
 * Bản xem lại toàn phiếu: đọc lại như phiếu giấy trước khi nộp (Doc/11a). Chỉ đọc, dựng lại mỗi lần mở
 * từ `surveyOutline`, không đụng tới DOM của màn nhập nên không có đường nào sửa nhầm câu trả lời.
 */
import { esc } from './util.js';
import { blockingItems, problemByKey, sectionBadge, sectionStates } from '../engine/review-model.js';

const numbered = (num, text) => `${num ? `${esc(num)}. ` : ''}${esc(text)}`;

function answerHtml(q, problems) {
  const problem = problems.get(q.key);
  if (problem === 'missing') return '<span class="rv-missing">Chưa trả lời</span>';
  if (!q.display) return '<span class="rv-blank">Không trả lời</span>';
  if (problem === 'invalid') return `<span class="rv-missing">${esc(q.display)} — giá trị không hợp lệ</span>`;
  return esc(q.display);
}

function cellHtml(q, problems) {
  if (!q.applicable) return '<td class="cell-na" title="Không áp dụng cho dòng này"></td>';
  if (!q.display && !problems.has(q.key)) return '<td></td>';
  return `<td data-review-key="${esc(q.key)}">${answerHtml(q, problems)}</td>`;
}

function repeatHtml(item, problems) {
  const namedRows = item.rows.some((r) => r.label !== null);
  const head = `<tr>
    <th class="record-col${namedRows ? '' : ' record-col-num'}">${namedRows ? 'Bản ghi' : 'STT'}</th>
    ${item.columns.map((c) => `<th>${esc(c.label)}${c.required ? '<span class="req">*</span>' : ''}</th>`).join('')}
  </tr>`;
  const rows = item.rows
    .map(
      (row) => `<tr>
        <td class="record-col${namedRows ? '' : ' record-col-num'}">${esc(row.label ?? row.index + 1)}</td>
        ${row.cells.map((c) => cellHtml(c, problems)).join('')}
      </tr>`,
    )
    .join('');
  const nested = item.rows
    .filter((row) => row.items.length)
    .map((row) => `<div class="rv-nested"><div class="rv-group-title">${esc(row.label ?? `Dòng ${row.index + 1}`)}</div>${itemsHtml(row.items, problems)}</div>`)
    .join('');
  const problem = problems.get(item.key);
  return `<div class="rv-repeat" data-review-key="${esc(item.key)}">
    <div class="rv-group-title">${numbered(item.num, item.label)}</div>
    ${problem ? '<div class="rv-missing">Số dòng vượt giới hạn của bảng.</div>' : ''}
    ${
      item.rows.length && item.columns.length
        ? `<div class="table-scroll"><table class="data-table repeat-table"><thead>${head}</thead><tbody>${rows}</tbody></table></div>`
        : item.rows.length
          ? ''
          : '<p class="rv-blank">Không có dòng nào.</p>'
    }
    ${nested}
  </div>`;
}

function itemsHtml(items, problems) {
  return items
    .map((item) => {
      if (item.kind === 'group') {
        return `<div class="rv-group"><div class="rv-group-title">${numbered(item.num, item.label)}</div>${itemsHtml(item.items, problems)}</div>`;
      }
      if (item.kind === 'repeat') return repeatHtml(item, problems);
      return `<div class="rv-q" data-review-key="${esc(item.key)}">
        <div class="rv-label">${numbered(item.num, item.label)}${item.required ? '<span class="req">*</span>' : ''}</div>
        <div class="rv-value">${answerHtml(item, problems)}</div>
      </div>`;
    })
    .join('');
}

/**
 * @param {HTMLElement} host
 * @param {object} args
 * @param {Array} args.outline - kết quả `surveyOutline`
 * @param {Array} args.findings - cùng tập với bảng cảnh báo
 * @param {boolean} args.readonly - phiếu đã khoá: không có "Sửa phần này" và danh sách còn thiếu
 * @param {(sectionId: string) => void} args.onEditSection
 * @param {(key: string) => void} args.onJumpToKey
 */
export function renderFormReview(host, { outline, findings, readonly, onEditSection, onJumpToKey }) {
  const problems = problemByKey(findings);
  const states = sectionStates(outline, findings);
  const blocking = readonly ? [] : blockingItems(outline, findings);

  host.innerHTML = `
    ${
      readonly
        ? ''
        : `<div class="status-banner tone-info"><span class="ms">description</span>
             <span>Đọc lại như phiếu giấy. Mục đỏ là câu bắt buộc chưa trả lời; bấm "Sửa phần này" để quay lại đúng chỗ.</span></div>`
    }
    ${outline
      .map((section) => {
        const badge = sectionBadge(states.get(section.id));
        return `<section class="rv-section" ${section.id ? `id="rev-${esc(section.id)}"` : ''}>
          ${
            section.label
              ? `<header class="rv-section-head">
                   <h2>${numbered(section.num, section.label)}</h2>
                   ${readonly ? '' : `<span class="badge rv-badge tone-${badge.tone}">${esc(badge.text)}</span>
                   <button type="button" class="btn btn-text sm" data-edit-section="${esc(section.id)}"><span class="ms">edit</span>Sửa phần này</button>`}
                 </header>`
              : ''
          }
          ${itemsHtml(section.items, problems)}
        </section>`;
      })
      .join('')}
    ${
      blocking.length
        ? `<div class="status-banner tone-error rv-blocking">
             <span class="ms">error</span>
             <div>
               <b>Còn ${blocking.length} mục chưa đạt nên chưa nộp được:</b>
               <ul>${blocking
                 .map((b) => `<li><button type="button" class="link-btn" data-jump-key="${esc(b.key ?? '')}">${esc(b.title)}</button></li>`)
                 .join('')}</ul>
             </div>
           </div>`
        : ''
    }`;

  host.querySelectorAll('[data-edit-section]').forEach((btn) =>
    btn.addEventListener('click', () => onEditSection(btn.dataset.editSection)),
  );
  host.querySelectorAll('[data-jump-key]').forEach((btn) =>
    btn.addEventListener('click', () => onJumpToKey(btn.dataset.jumpKey)),
  );
}
